// ============================================================================
// OPTIMIZED DASHBOARD.TSX - PERFORMANCE ENHANCED + ADMIN CHANGELOG
// ============================================================================

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { AuthUser } from '../../services/auth.service';
import Header from './Header';
import Sidebar from './Sidebar';
import MainContent from './MainContent';
import styles from './Dashboard.module.css';
// 🆕 NEW IMPORTS FOR ADMIN CHANGELOG
import AdminChangeLogModal from './modals/AdminChangeLogModal';
import { changeLogService, ChangeLog } from '../../services/changeLog.service';

interface DashboardProps {
  user: AuthUser;
  onLogout: () => void;
}

interface NavigationParams {
  year?: number;
  month?: string;
  employeeId?: string;
  requestId?: string;
  overtimeFilters?: {
    startDate?: string;
    endDate?: string;
    status?: string;
  };
  [key: string]: any;
}

const Dashboard: React.FC<DashboardProps> = React.memo(({ user, onLogout }) => {
  const [activePage, setActivePage] = useState('dashboard');
  const [employeeId, setEmployeeId] = useState<string | undefined>(undefined);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // 🆕 NEW STATE FOR ADMIN CHANGELOG
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [activeChangeLog, setActiveChangeLog] = useState<ChangeLog | null>(null);

  // ============================================================================
  // 🆕 NEW: CHECK FOR ADMIN CHANGELOG ON MOUNT
  // ============================================================================
  useEffect(() => {
    checkForChangeLog();
  }, []);

  const checkForChangeLog = async () => {
    const userId = user?.employeeNo || user?.employeeNumber || user?.uid;
    if (!userId) return;

    try {
      const result = await changeLogService.shouldShowChangeLog(userId);
      
      if (result.shouldShow && result.changeLog) {
        setActiveChangeLog(result.changeLog);
        setShowChangeLog(true);
        console.log('📋 Showing admin change log to user:', userId);
      }
    } catch (error) {
      console.error('Error checking admin change log:', error);
    }
  };

  const handleChangeLogAcknowledge = () => {
    setShowChangeLog(false);
    setActiveChangeLog(null);
    console.log('✅ Admin acknowledged change log');
  };

  // ============================================================================
  // OPTIMIZED NAVIGATION HANDLERS
  // ============================================================================

  const handlePageChange = useCallback((pageId: string, params?: string | NavigationParams) => {
    if (isTransitioning) {
      return;
    }
    
    setIsTransitioning(true);
    
    let newEmployeeId: string | undefined;
    
    if (typeof params === 'string') {
      newEmployeeId = params;
    } else if (params && typeof params === 'object') {
      if (params.year && params.month) {
        newEmployeeId = `${params.year}-${params.month}`;
      } else if (params.year) {
        newEmployeeId = params.year.toString();
      } else if (params.requestId) {
        newEmployeeId = params.requestId;
      } else if (params.overtimeFilters) {
        newEmployeeId = JSON.stringify(params.overtimeFilters);
      } else if (params.employeeId) {
        newEmployeeId = params.employeeId;
      }
    }
    
    if (pageId === activePage && newEmployeeId === employeeId) {
      setIsTransitioning(false);
      return;
    }
    
    setActivePage(pageId);
    setEmployeeId(newEmployeeId);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 150);
  }, [activePage, employeeId, isTransitioning]);

  const handleSpecialNavigation = useCallback((page: string, params?: any) => {
    switch (page) {
      case 'hiring-analytics':
        handlePageChange('hiring-analytics');
        break;
      case 'hiring-analytics-year':
        if (params?.year) {
          handlePageChange('hiring-analytics-year', { year: params.year });
        }
        break;
      case 'hiking-analytics-month':
        if (params?.year && params?.month) {
          handlePageChange('hiring-analytics-month', { 
            year: params.year, 
            month: params.month 
          });
        }
        break;
      case 'overtime-reports':
        handlePageChange('overtime-reports');
        break;
      case 'overtime-reports-filtered':
        if (params?.filters) {
          handlePageChange('overtime-reports', { overtimeFilters: params.filters });
        } else {
          handlePageChange('overtime-reports');
        }
        break;
      case 'overtime-request-detail':
        if (params?.requestId) {
          handlePageChange('overtime-request-detail', { requestId: params.requestId });
        }
        break;
      case 'employee-profile':
        let employeeId: string | undefined;
        
        if (typeof params === 'string') {
          employeeId = params;
        } else if (params?.employeeId) {
          employeeId = params.employeeId;
        }
        
        if (employeeId) {
          handlePageChange('employee-profile', employeeId);
        }
        break;
      case 'employee-vacation-booking':
        let vacationEmployeeId: string | undefined;
        
        if (typeof params === 'string') {
          vacationEmployeeId = params;
        } else if (params?.employeeId) {
          vacationEmployeeId = params.employeeId;
        }
        
        if (vacationEmployeeId) {
          handlePageChange('employee-vacation-booking', vacationEmployeeId);
        }
        break;
      case 'employee-nationalities':
        handlePageChange('employee-nationalities');
        break;
      case 'location-exemptions':
        handlePageChange('location-exemptions');
        break;
      case 'celebrations':
        handlePageChange('celebrations');
        break;
      default:
        handlePageChange(page, params);
        break;
    }
  }, [handlePageChange]);

  const handleSidebarPageChange = useCallback((pageId: string) => {
    handlePageChange(pageId, undefined);
  }, [handlePageChange]);

  const handleSidebarToggle = useCallback((collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
  }, []);

  // ============================================================================
  // MEMOIZED PROPS
  // ============================================================================

  const sidebarProps = useMemo(() => ({
    activePage,
    onPageChange: handleSidebarPageChange,
    isTransitioning,
    onToggle: handleSidebarToggle,
    user
  }), [activePage, handleSidebarPageChange, isTransitioning, handleSidebarToggle, user]);

  const mainContentProps = useMemo(() => ({
    activePage,
    employeeId,
    onNavigate: handleSpecialNavigation,
    isTransitioning,
    isSidebarCollapsed,
    user
  }), [activePage, employeeId, handleSpecialNavigation, isTransitioning, isSidebarCollapsed, user]);

  const headerProps = useMemo(() => ({
    user,
    onLogout,
    onNavigate: handleSpecialNavigation
  }), [user, onLogout, handleSpecialNavigation]);

  // ============================================================================
  // OPTIMIZED: Removed debug logging and unnecessary effects
  // ============================================================================

  // Page title management
  useEffect(() => {
    const baseTitle = 'PHOENICIAN HR Dashboard';
    let pageTitle = baseTitle;
    
    if (activePage !== 'dashboard') {
      const pageName = activePage
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      pageTitle = `${pageName} | ${baseTitle}`;
    }
    
    document.title = pageTitle;
  }, [activePage]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className={styles.dashboardContainer}>
      <Header {...headerProps} />
      <div className={styles.dashboardBody}>
        <Sidebar {...sidebarProps} />
        <MainContent {...mainContentProps} />
      </div>

      {/* 🆕 NEW: ADMIN CHANGELOG MODAL */}
      {showChangeLog && activeChangeLog && (
        <AdminChangeLogModal
          changeLog={activeChangeLog}
          user={user}
          onAcknowledge={handleChangeLogAcknowledge}
        />
      )}
    </div>
  );
});

Dashboard.displayName = 'Dashboard';
export default Dashboard;